<template>
  <div class="tag-panel">
    <div class="tag-panel__selection">
      <template v-if="selectedCount">
        <strong>{{ selectedCount }}</strong> box<span v-if="selectedCount !== 1">es</span> selected
        <span class="text--secondary"> · {{ selectedTaggedCount }} already tagged</span>
      </template>
      <span v-else class="text--secondary">
        Select boxes to tag — drag a box around them, or click one at a time
      </span>
    </div>

    <v-select
      :value="activeCategoryId"
      :items="categoryOptions"
      item-text="name"
      item-value="id"
      dense
      outlined
      hide-details
      label="Category"
      class="tag-panel__category-select"
      @change="$emit('set-category', $event)"
    />

    <!--
      "Narrow by" hierarchical facets: when the active category rolls up to
      hierarchical ancestors (e.g. Sub-Brand → Brand + Category), let the tagger
      keep only the tags under a chosen ancestor (e.g. only Paper-Towel
      sub-brands). View-only — it never applies a tag.
    -->
    <div v-if="hierarchyFacets.length" class="tag-panel__facets">
      <v-select
        v-for="facet in hierarchyFacets"
        :key="facet.category.id"
        :value="facetSelected(facet)"
        :items="facet.tags"
        item-text="name"
        item-value="id"
        multiple
        chips
        small-chips
        deletable-chips
        dense
        outlined
        hide-details
        clearable
        :label="'Narrow by ' + facet.category.name"
        class="tag-panel__facet"
        @change="onFacetChange(facet, $event)"
      />
    </div>

    <v-text-field
      v-model="search"
      ref="search"
      dense
      outlined
      hide-details
      clearable
      prepend-inner-icon="mdi-magnify"
      label="Filter tags"
      class="tag-panel__search"
    />

    <div class="tag-panel__list">
      <div v-for="category in filteredCategories" :key="category.id" class="tag-panel__category">
        <div class="tag-panel__category-name">{{ category.name }}</div>
        <div
          v-for="tag in category.tags"
          :key="tag.id"
          class="tag-panel__tag"
          :class="{'is-disabled': !selectedCount}"
          @click="cycleTag(tag)"
        >
          <v-simple-checkbox
            :value="checkboxValue(tag.id)"
            :indeterminate="isIndeterminate(tag.id)"
            :disabled="!selectedCount"
            :ripple="false"
            dense
            @click.stop="cycleTag(tag)"
          />
          <span class="tag-panel__tag-name">{{ tag.name }}</span>
          <span v-if="pendingIntent(tag.id)" class="tag-panel__intent" :class="'is-' + pendingIntent(tag.id)">
            {{ pendingIntent(tag.id) === 'add' ? '+' : '−' }}
          </span>
        </div>
      </div>

      <div v-if="!activeCategoryId" class="tag-panel__empty text--secondary">
        Choose a category above to start tagging.
      </div>
      <div v-else-if="!filteredCategories.length" class="tag-panel__empty text--secondary">
        No tags match "{{ search }}"
      </div>
    </div>

    <div class="tag-panel__actions">
      <v-btn
        color="primary"
        block
        :disabled="!canApply"
        :loading="isSaving"
        @click="$emit('apply')"
      >
        {{ applyLabel }}
      </v-btn>

      <div class="tag-panel__reject">
        <v-btn-toggle :value="rejectReason" mandatory dense class="tag-panel__reason" @change="$emit('set-reject-reason', $event)">
          <v-btn small value="invalid">Invalid</v-btn>
          <v-btn small value="duplicate">Duplicate</v-btn>
        </v-btn-toggle>
        <v-btn small text block :disabled="!selectedCount" class="tag-panel__reject-btn" @click="$emit('reject', rejectReason)">
          <v-icon small left>mdi-close-circle-outline</v-icon>
          Reject as {{ rejectReason }}
        </v-btn>
      </div>
      <div class="tag-panel__secondary">
        <v-btn small text block :disabled="!selectedCount" @click="$emit('clear-selection')">
          Deselect
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BoxTagPanel',

  props: {
    categories: {type: Array, default: () => []},   // [{id, name, tags: [{id, name}]}]
    activeCategoryId: {default: null},               // null = all categories
    rejectReason: {type: String, default: 'invalid'}, // 'invalid' | 'duplicate'
    selectedCount: {type: Number, default: 0},
    selectedTaggedCount: {type: Number, default: 0},
    pendingTags: {type: Object, default: () => ({})},
    tagState: {type: Function, required: true},      // (tagId) => 'all' | 'some' | 'none'
    isSaving: {type: Boolean, default: false},
    // "Narrow by" hierarchical ancestor filter.
    hierarchyFacets: {type: Array, default: () => []},   // [{category:{id,name}, tags:[{id,name}]}]
    selectedHierarchyIds: {type: Array, default: () => []},
    ancestorMap: {type: Object, default: () => ({})},    // {tagId: [ancestorTagId,...]}
  },

  data() {
    return {search: ''}
  },

  computed: {
    categoryOptions() {
      // A category must be chosen before tagging — no 'All categories' escape
      // hatch, and drop the null 'Uncategorised' bucket so there is no null option.
      return this.categories.filter(c => c.id != null).map(c => ({id: c.id, name: c.name}))
    },

    filteredCategories() {
      const term = (this.search || '').trim().toLowerCase()
      const scoped = this.activeCategoryId == null
        ? []
        : this.categories.filter(c => c.id === this.activeCategoryId)
      return scoped
        .map(category => {
          let tags = category.tags || []
          if (term) tags = tags.filter(tag => tag.name.toLowerCase().includes(term))
          tags = tags.filter(tag => this.passesFacets(tag.id))
          return {...category, tags}
        })
        .filter(category => category.tags.length)
    },

    pendingAddCount() {
      return Object.values(this.pendingTags).filter(v => v === 'add').length
    },

    pendingRemoveCount() {
      return Object.values(this.pendingTags).filter(v => v === 'remove').length
    },

    canApply() {
      return this.selectedCount > 0 && (this.pendingAddCount > 0 || this.pendingRemoveCount > 0)
    },

    applyLabel() {
      if (!this.canApply) return 'Apply'
      const parts = []
      if (this.pendingAddCount) parts.push(`${this.pendingAddCount} add`)
      if (this.pendingRemoveCount) parts.push(`${this.pendingRemoveCount} remove`)
      return `Apply · ${parts.join(' · ')}`
    },
  },

  methods: {
    pendingIntent(tagId) {
      return this.pendingTags[tagId] || null
    },

    // --- "narrow by" hierarchical facets ---------------------------------

    /** The currently-selected values belonging to one facet. */
    facetSelected(facet) {
      const ids = facet.tags.map(t => t.id)
      return this.selectedHierarchyIds.filter(id => ids.includes(id))
    },

    /** Replace this facet's slice of the selection, keeping the other facets. */
    onFacetChange(facet, ids) {
      const facetIds = facet.tags.map(t => t.id)
      const others = this.selectedHierarchyIds.filter(id => !facetIds.includes(id))
      this.$emit('set-hierarchy-filter', others.concat(ids || []))
    },

    /**
     * Faceted narrowing: a tag passes when, for every CONSTRAINED facet (one
     * with a selection), the tag's ancestors include at least one of that
     * facet's selected values. AND across facets, OR within a facet.
     */
    passesFacets(tagId) {
      if (!this.selectedHierarchyIds.length) return true
      const anc = this.ancestorMap[tagId] || []
      for (const facet of this.hierarchyFacets) {
        const facetIds = facet.tags.map(t => t.id)
        const selectedInFacet = this.selectedHierarchyIds.filter(id => facetIds.includes(id))
        if (!selectedInFacet.length) continue
        if (!selectedInFacet.some(id => anc.includes(id))) return false
      }
      return true
    },

    /** Checked when every selected box has it, or when it is pending-add. */
    checkboxValue(tagId) {
      const intent = this.pendingIntent(tagId)
      if (intent === 'add') return true
      if (intent === 'remove') return false
      return this.tagState(tagId) === 'all'
    },

    isIndeterminate(tagId) {
      if (this.pendingIntent(tagId)) return false
      return this.tagState(tagId) === 'some'
    },

    /**
     * Click cycles intent based on where the tag currently stands:
     *   none/some -> add   (bring the whole selection up to having it)
     *   all       -> remove
     *   pending   -> back to no-change
     */
    cycleTag(tag) {
      if (!this.selectedCount) return
      this.$emit('cycle-tag', tag.id)
    },

    focusSearch() {
      this.$refs.search && this.$refs.search.focus()
    },
  },
}
</script>

<style scoped>
.tag-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.tag-panel__selection {
  padding: 8px 4px 12px;
  font-size: 13px;
  line-height: 1.4;
  min-height: 44px;
}

.tag-panel__category-select {
  flex: 0 0 auto;
  margin-bottom: 8px;
}

.tag-panel__search {
  flex: 0 0 auto;
  margin-bottom: 8px;
}

.tag-panel__facets {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
}

.tag-panel__list {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  padding: 4px 0;
}

.tag-panel__category-name {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(0, 0, 0, 0.55);
  padding: 8px 12px 2px;
}

.tag-panel__tag {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 1px 12px 1px 6px;
  cursor: pointer;
  font-size: 13px;
}

.tag-panel__tag:hover {
  background: rgba(0, 0, 0, 0.04);
}

.tag-panel__tag.is-disabled {
  cursor: default;
  opacity: 0.5;
}

.tag-panel__tag.is-disabled:hover {
  background: transparent;
}

.tag-panel__tag-name {
  flex: 1 1 auto;
}

.tag-panel__intent {
  flex: 0 0 auto;
  font-weight: 700;
  width: 12px;
  text-align: center;
}

.tag-panel__intent.is-add { color: var(--v-success-base); }
.tag-panel__intent.is-remove { color: var(--v-error-base); }

.tag-panel__empty {
  padding: 16px 12px;
  font-size: 13px;
}

.tag-panel__actions {
  flex: 0 0 auto;
  padding-top: 12px;
}

.tag-panel__secondary {
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
}
/* Stacked, not side-by-side: the Invalid/Duplicate toggle + "Reject as …"
   button could not both fit the 300px sidebar on one row, so the button label
   clipped to "Reject as Dup"/"Reject as Inva". */
.tag-panel__reject {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  margin-top: 8px;
}

.tag-panel__reason {
  display: flex;
}

.tag-panel__reason >>> .v-btn {
  flex: 1 1 0;
}

.tag-panel__reject-btn {
  text-transform: capitalize;
}

/* Dark mode (restyle step 5): the list border, category label grey and the
   row-hover tint are all black-based and disappear on a dark surface - flip to
   white alphas. The is-add/is-remove intents use theme tokens, so they adapt
   already. .theme--dark is the whole-app dark class on the v-app ancestor. */
.theme--dark .tag-panel__list { border-color: rgba(255, 255, 255, 0.18); }
.theme--dark .tag-panel__category-name { color: rgba(255, 255, 255, 0.6); }
.theme--dark .tag-panel__tag:hover { background: rgba(255, 255, 255, 0.06); }
</style>
